// ── The living battlefield behind the war table ──
// Parallax depth layers: storm sky → mountains → besieged castle → fog →
// two marching armies → battle ground → embers, smoke and torchlight.

import { useMemo, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { useParallax, useAmbientBeat, usePrefersReducedMotion, seeded } from '../../lib/battle3d';

// Per-kingdom colour grading for the whole battlefield
const FIELD_THEME = {
  Italy:   { sky: ['#01060a', '#03110e', '#0a2a1e'], haze: 'rgba(0,190,110,0.34)', torch: '#8fe8a0', banner: '#009246', banner2: '#CE2B37', moon: 'rgba(214,255,228,0.9)' },
  Spain:   { sky: ['#07030a', '#150604', '#3a1608'], haze: 'rgba(255,145,20,0.36)', torch: '#ffb347', banner: '#c60b1e', banner2: '#ffc400', moon: 'rgba(255,232,190,0.9)' },
  France:  { sky: ['#01030c', '#03081e', '#0a1642'], haze: 'rgba(70,120,255,0.34)', torch: '#8ec0ff', banner: '#002395', banner2: '#ED2939', moon: 'rgba(214,228,255,0.9)' },
  England: { sky: ['#070206', '#16050a', '#380d13'], haze: 'rgba(220,45,75,0.32)', torch: '#ff9b8a', banner: '#C8102E', banner2: '#f8fafc', moon: 'rgba(255,220,220,0.9)' },
  Unknown: { sky: ['#080602', '#150e05', '#2e1f0a'], haze: 'rgba(251,175,36,0.30)', torch: '#ffcf6b', banner: '#fbbf24', banner2: '#92400e', moon: 'rgba(255,242,208,0.9)' },
};

export const getFieldTheme = (kingdom) => FIELD_THEME[kingdom] || FIELD_THEME.Unknown;

// ── Silhouette of a single foot soldier, optionally carrying a banner ──
const Soldier = ({ x, scale, banner, color, flip }) => (
  <g transform={`translate(${x} 0) scale(${flip ? -scale : scale} ${scale})`}>
    {/* spear / banner pole */}
    <rect x="12" y="-34" width="2.2" height="76" fill="#000" opacity="0.98" />
    {banner ? (
      <path d="M13.8 -28 q10 3 18 0 q-3 7 0 14 q-9 3 -18 0 z" fill={color} opacity="0.85" />
    ) : (
      <path d="M13.1 -40 l-3 7 l3 5.4 l3 -5.4 z" fill="#000" />
    )}
    {/* shoulders + torso */}
    <path d="M1 42 L1 17 Q1 9 7 9 Q13 9 13 17 L13 42 Z" fill="#000" opacity="0.96" />
    {/* shield on the arm */}
    <path d="M-2 20 q-4 1 -4 6 q0 7 4 9 q4 -2 4 -9 q0 -5 -4 -6 z" fill="#000" opacity="0.9" />
    <path d="M-2 21 q-3 1 -3 5 q0 5 3 7 q3 -2 3 -7 q0 -4 -3 -5 z" fill={color} opacity="0.32" />
    {/* helmet with crest */}
    <circle cx="7" cy="5.5" r="5.2" fill="#000" />
    <path d="M4 1.5 q3 -5 6 0" stroke={color} strokeWidth="1.2" fill="none" opacity="0.5" />
  </g>
);

const ArmyRow = ({ count, seed, color, opacity, blur, swayDur, flip = false, bottom, heightPx }) => {
  const soldiers = useMemo(() => {
    const rnd = seeded(seed);
    return Array.from({ length: count }, (_, i) => ({
      x: (i / count) * 1000 + rnd() * 26,
      scale: 0.82 + rnd() * 0.4,
      banner: rnd() > 0.82,
      phase: rnd() * 2,
    }));
  }, [count, seed]);

  return (
    <div style={{ position: 'absolute', left: '-6%', right: '-6%', bottom, height: heightPx, opacity, filter: blur ? `blur(${blur}px)` : 'none', pointerEvents: 'none' }}>
      <svg viewBox="0 0 1000 46" preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
        {soldiers.map((s, i) => (
          <motion.g
            key={i}
            animate={{ y: [0, -1.6, 0], rotate: [-0.5, 0.5, -0.5] }}
            transition={{ duration: swayDur + s.phase, repeat: Infinity, ease: 'easeInOut', delay: s.phase }}
            style={{ transformOrigin: `${s.x}px 42px` }}
          >
            <Soldier x={s.x} scale={s.scale} banner={s.banner} color={color} flip={flip} />
          </motion.g>
        ))}
      </svg>
    </div>
  );
};

// ── Besieged castle on the horizon ──
const Castle = ({ color, torch }) => (
  <svg viewBox="0 0 400 200" preserveAspectRatio="xMidYMax meet" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
    <defs>
      <linearGradient id="castle-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#050505" />
        <stop offset="100%" stopColor="#000" />
      </linearGradient>
    </defs>
    {/* keep + curtain wall */}
    <path
      d="M40 200 L40 120 L48 120 L48 110 L58 110 L58 120 L66 120 L66 100 L74 100 L74 110 L84 110 L84 100 L92 100 L92 120 L110 120 L110 70 L118 70 L118 58 L128 58 L128 70 L140 70 L140 52 L150 52 L150 40 L162 40 L162 52 L174 52 L174 70 L186 70 L186 58 L196 58 L196 70 L206 70 L206 120 L226 120 L226 100 L234 100 L234 110 L244 110 L244 100 L252 100 L252 120 L262 120 L262 110 L272 110 L272 120 L282 120 L282 200 Z"
      fill="url(#castle-body)"
    />
    {/* corner towers */}
    <path d="M24 200 L24 92 L30 92 L30 84 L38 84 L38 92 L46 92 L46 200 Z" fill="#000" />
    <path d="M276 200 L276 92 L282 92 L282 84 L290 84 L290 92 L298 92 L298 200 Z" fill="#000" />
    {/* lit windows */}
    {[[56, 140], [76, 148], [132, 96], [152, 88], [172, 96], [232, 142], [258, 150], [32, 118], [286, 118]].map(([x, y], i) => (
      <motion.rect
        key={i} x={x} y={y} width="5" height="8" rx="2" fill={torch}
        animate={{ opacity: [0.35, 0.95, 0.5, 0.85, 0.35] }}
        transition={{ duration: 2.4 + i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
      />
    ))}
    {/* waving banners on the keep */}
    {[[146, 40], [166, 52]].map(([x, y], i) => (
      <g key={`b${i}`}>
        <rect x={x} y={y - 26} width="1.6" height="28" fill="#000" />
        <motion.path
          d={`M${x + 1.6} ${y - 25} q9 3 16 0 q-3 6 0 12 q-8 3 -16 0 z`}
          fill={color} opacity="0.75"
          animate={{ skewY: [-4, 4, -4], scaleX: [1, 0.88, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
          style={{ transformOrigin: `${x + 1.6}px ${y - 19}px` }}
        />
      </g>
    ))}
  </svg>
);

// ── Mountain ridge ──
const Ridge = ({ opacity, color }) => (
  <svg viewBox="0 0 1000 180" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
    <path
      d="M0 180 L0 120 L70 62 L128 104 L196 40 L260 96 L330 54 L398 110 L470 68 L540 118 L610 74 L690 120 L760 86 L840 126 L910 92 L1000 132 L1000 180 Z"
      fill={color} opacity={opacity}
    />
  </svg>
);

const BattleField = ({ kingdom = 'Unknown', mobile = false, dimmed = false }) => {
  const theme = getFieldTheme(kingdom);
  const reduced = usePrefersReducedMotion();
  const { px, py } = useParallax(!reduced);
  const [flash, setFlash] = useState(0);

  // Distant thunder / catapult impacts
  useAmbientBeat(() => setFlash(Date.now()), { min: 7000, max: 16000, enabled: !reduced });

  // Parallax offsets — one pair per depth layer (hooks stay at the top level)
  const skyX = useTransform(px, (v) => v * -6);
  const skyY = useTransform(py, (v) => v * -2.7);
  const ridgeFarX = useTransform(px, (v) => v * -14);
  const ridgeFarY = useTransform(py, (v) => v * -6.3);
  const ridgeNearX = useTransform(px, (v) => v * -24);
  const ridgeNearY = useTransform(py, (v) => v * -10.8);
  const castleX = useTransform(px, (v) => v * -34);
  const castleY = useTransform(py, (v) => v * -15.3);
  const armyFarX = useTransform(px, (v) => v * -48);
  const armyFarY = useTransform(py, (v) => v * -21.6);
  const armyNearX = useTransform(px, (v) => v * -72);
  const armyNearY = useTransform(py, (v) => v * -32.4);
  const groundX = useTransform(px, (v) => v * -96);
  const groundY = useTransform(py, (v) => v * -43.2);

  const sky = { x: skyX, y: skyY };
  const ridgeFar = { x: ridgeFarX, y: ridgeFarY };
  const ridgeNear = { x: ridgeNearX, y: ridgeNearY };
  const castle = { x: castleX, y: castleY };
  const armyFar = { x: armyFarX, y: armyFarY };
  const armyNear = { x: armyNearX, y: armyNearY };
  const ground = { x: groundX, y: groundY };

  const stars = useMemo(() => {
    const rnd = seeded(77);
    return Array.from({ length: mobile ? 26 : 54 }, (_, i) => ({ i, x: rnd() * 100, y: rnd() * 30, s: rnd() * 1.6 + 0.6, d: 2 + rnd() * 4 }));
  }, [mobile]);

  const emberCount = reduced ? 0 : mobile ? 16 : 34;
  const embers = useMemo(() => {
    const rnd = seeded(20260921);
    return Array.from({ length: emberCount }, () => ({
      x: rnd() * 100,
      size: 1.5 + rnd() * 3.5,
      dur: 6 + rnd() * 9,
      delay: rnd() * 10,
      drift: (rnd() - 0.5) * 120,
    }));
  }, [emberCount]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>

      {/* ── Deep night behind everything ── */}
      <div style={{ position: 'absolute', inset: 0, background: '#010306' }} />

      {/* ── Storm sky, ending at the horizon ── */}
      <motion.div style={{ position: 'absolute', top: '-8%', left: '-8%', right: '-8%', height: '82%', ...sky,
        background: `radial-gradient(ellipse 110% 55% at 50% 100%, ${theme.haze} 0%, transparent 68%),
                     linear-gradient(to bottom, ${theme.sky[0]} 0%, ${theme.sky[1]} 52%, ${theme.sky[2]} 100%)` }} />

      {/* Stars */}
      {!reduced && (
        <div style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
          {stars.map(s => (
            <motion.div key={s.i}
              animate={{ opacity: [0.15, 0.85, 0.15] }}
              transition={{ duration: s.d, repeat: Infinity, delay: s.i * 0.17 }}
              style={{ position: 'absolute', left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s, borderRadius: '50%', background: '#fff' }}
            />
          ))}
        </div>
      )}

      {/* Moon */}
      <motion.div style={{ position: 'absolute', top: '5%', right: '14%', width: mobile ? 70 : 110, height: mobile ? 70 : 110, borderRadius: '50%', ...sky,
        background: `radial-gradient(circle at 42% 38%, ${theme.moon} 0%, rgba(255,255,255,0.22) 42%, rgba(255,255,255,0.06) 62%, transparent 76%)`,
        filter: 'blur(3px)', opacity: 0.55 }} />

      {/* ── Lightning flash ── */}
      <motion.div key={flash} initial={{ opacity: 0 }}
        animate={flash ? { opacity: [0, 0.5, 0.08, 0.34, 0] } : {}}
        transition={{ duration: 0.9, times: [0, 0.06, 0.16, 0.24, 1] }}
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(200,225,255,0.8), transparent 60%)', mixBlendMode: 'screen' }} />

      {/* ── Glow along the horizon so every silhouette reads ── */}
      <div style={{
        position: 'absolute', left: '-10%', right: '-10%', bottom: '61%', height: '13%',
        background: `radial-gradient(ellipse 60% 100% at 50% 100%, ${theme.haze} 0%, transparent 74%)`,
        filter: 'blur(16px)', opacity: 0.75,
      }} />

      {/* ── Mountains ── */}
      <motion.div style={{ position: 'absolute', left: '-6%', right: '-6%', bottom: '66%', height: '17%', ...ridgeFar }}>
        <Ridge opacity={0.6} color="#05070c" />
      </motion.div>
      <motion.div style={{ position: 'absolute', left: '-8%', right: '-8%', bottom: '64%', height: '14%', ...ridgeNear }}>
        <Ridge opacity={0.95} color="#000" />
      </motion.div>

      {/* ── Castle ── */}
      <motion.div style={{ position: 'absolute', left: '50%', bottom: '64%', width: mobile ? 250 : 440, height: mobile ? 110 : 190, marginLeft: mobile ? -125 : -220, ...castle }}>
        <Castle color={theme.banner} torch={theme.torch} />
      </motion.div>

      {/* ── Horizon fog ── */}
      {[0, 1].map(i => (
        <motion.div key={i}
          animate={reduced ? {} : { x: ['-8%', '8%', '-8%'], opacity: [0.16, 0.3, 0.16] }}
          transition={{ duration: 26 + i * 12, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', left: '-15%', right: '-15%', bottom: `${55 + i * 4}%`, height: '14%',
            background: `radial-gradient(ellipse 60% 100% at ${30 + i * 40}% 60%, ${theme.haze} 0%, transparent 70%)`,
            filter: 'blur(24px)' }} />
      ))}

      {/* ── The armies ── */}
      <motion.div style={{ position: 'absolute', left: 0, right: 0, bottom: '63%', height: mobile ? 40 : 60, ...armyFar }}>
        <ArmyRow count={mobile ? 18 : 34} seed={4411} color={theme.banner2} opacity={0.72} blur={1} swayDur={3.4} bottom={0} heightPx="100%" />
      </motion.div>
      <motion.div style={{ position: 'absolute', left: 0, right: 0, bottom: '58%', height: mobile ? 54 : 86, ...armyNear }}>
        <ArmyRow count={mobile ? 13 : 24} seed={9182} color={theme.banner} opacity={0.95} blur={0} swayDur={4.1} bottom={0} heightPx="100%" />
      </motion.div>

      {/* ── Battle ground (tilted plane) ── */}
      <motion.div style={{ position: 'absolute', left: '-20%', right: '-20%', bottom: 0, height: '62%', ...ground,
        transform: 'perspective(900px) rotateX(52deg)', transformOrigin: 'center top',
        background: `linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(14,10,6,0.86) 40%, rgba(6,4,2,0.96) 100%)`,
        backgroundImage: `repeating-linear-gradient(to bottom, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 30px),
                          radial-gradient(ellipse 45% 45% at 50% 6%, ${theme.haze} 0%, transparent 62%),
                          linear-gradient(to bottom, rgba(2,3,5,0.94), rgba(1,1,2,0.99))` }} />

      {/* ── Rising embers ── */}
      {embers.map((e, i) => (
        <motion.div key={i}
          initial={{ opacity: 0 }}
          animate={{ y: ['0vh', '-72vh'], x: [0, e.drift], opacity: [0, 0.9, 0.7, 0] }}
          transition={{ duration: e.dur, repeat: Infinity, delay: e.delay, ease: 'linear' }}
          style={{ position: 'absolute', left: `${e.x}%`, bottom: '-2%', width: e.size, height: e.size, borderRadius: '50%',
            background: theme.torch, boxShadow: `0 0 ${e.size * 3}px ${theme.torch}` }} />
      ))}

      {/* ── Torchlight from both wings ── */}
      {['left', 'right'].map((side, i) => (
        <motion.div key={side}
          animate={reduced ? {} : { opacity: [0.35, 0.65, 0.4, 0.6, 0.35] }}
          transition={{ duration: 1.8 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', [side]: '-6%', bottom: '-6%', width: '26%', height: '38%',
            background: `radial-gradient(ellipse at ${side === 'left' ? '10%' : '90%'} 90%, rgba(255,146,46,0.20) 0%, rgba(255,110,20,0.07) 38%, transparent 72%)`,
            filter: 'blur(16px)' }} />
      ))}

      {/* ── Drifting battlefield smoke ── */}
      {!reduced && [0, 1, 2].map(i => (
        <motion.div key={`s${i}`}
          animate={{ x: ['-25%', '25%'], opacity: [0, 0.22, 0] }}
          transition={{ duration: 30 + i * 9, repeat: Infinity, delay: i * 7, ease: 'easeInOut' }}
          style={{ position: 'absolute', left: `${i * 30 - 10}%`, bottom: `${6 + i * 9}%`, width: '55%', height: '30%',
            background: 'radial-gradient(ellipse at center, rgba(120,110,100,0.35) 0%, transparent 70%)', filter: 'blur(32px)' }} />
      ))}

      {/* ── Night grade over the foreground ── */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 42%, rgba(0,0,0,0.42) 72%, rgba(0,0,0,0.7) 100%)' }} />

      {/* ── Vignette + darkening ── */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, transparent 38%, rgba(0,0,0,0.45) 76%, rgba(0,0,0,0.8) 100%)' }} />
      {dimmed && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />}
    </div>
  );
};

export default BattleField;
